<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { timeSeriesApi, aiApi } from '../lib/api';
  import type { DataPoint, CSVImportOptions } from '../lib/types';
  import { convertToTimeSeriesDataPoint } from '../lib/types';
  import { parseCSV, isValidNumber, isValidTimestamp } from '../lib/utils';
  
  export let timeseriesId: string;
  export let frequency: string = 'daily';
  
  const dispatch = createEventDispatcher();
  
  // Form state
  let csvFile: File | null = null;
  const csvOptions: CSVImportOptions = {
    timestamp_col: 'timestamp',
    value_col: 'value',
    interpolation_method: 'linear'
  };
  let manualDataPoint: DataPoint = {
    timestamp: new Date().toISOString().slice(0, 16),
    value: 0
  };
  let bulkDataPoints: string = '';
  // Progress tracking (commented out for now as it's not being used)
  // let uploadProgress: number = 0;
  let previewData: DataPoint[] = [];
  let loading: boolean = false;
  let recognizingColumns: boolean = false;
  let error: string = '';
  let success: string = '';
  let activeTab = 'manual';
  
  // Reset form function (keeping for future use)
  // function resetForm() {
  //   manualDataPoint = {
  //     timestamp: new Date().toISOString().slice(0, 16),
  //     value: 0
  //   };
  //   bulkDataPoints = '';
  //   csvFile = null;
  //   previewData = [];
  //   error = '';
  //   success = '';
  // }
  
  async function handleManualSubmit() {
    if (!isValidTimestamp(String(manualDataPoint.timestamp))) {
      error = 'Please enter a valid timestamp';
      return;
    }
    
    if (!isValidNumber(String(manualDataPoint.value))) {
      error = 'Please enter a valid number for value';
      return;
    }
    
    try {
      loading = true;
      error = '';
      success = '';
      
      await timeSeriesApi.addDataPoints(timeseriesId, [convertToTimeSeriesDataPoint(manualDataPoint)]);
      
      success = 'Data point added successfully!';
      dispatch('dataImported');
      manualDataPoint = {
        timestamp: new Date().toISOString().slice(0, 16),
        value: 0
      };
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to add data point';
    } finally {
      loading = false;
    }
  }
  
  async function handleBulkSubmit() {
    if (!bulkDataPoints.trim()) {
      error = 'Please enter some data points';
      return;
    }
    
    try {
      loading = true;
      error = '';
      success = '';
      
      // Parse the bulk input (expected format: timestamp,value on each line)
      const lines = bulkDataPoints.trim().split('\n');
      const dataPoints: DataPoint[] = [];
      
      for (const line of lines) {
        const parts = line.split(',').map(s => s.trim());
        if (parts.length < 2) {
          error = `Invalid data: ${line}. Format should be timestamp,value`;
          loading = false;
          return;
        }
        
        const timestamp = parts[0];
        const valueStr = parts[1] || '';
        const value = parseFloat(valueStr);
        
        if (!timestamp || !isValidTimestamp(String(timestamp)) || isNaN(value)) {
          error = `Invalid data: ${line}. Format should be timestamp,value`;
          loading = false;
          return;
        }
        
        dataPoints.push({ timestamp, value });
      }
      
      const convertedDataPoints = dataPoints.map(dp => convertToTimeSeriesDataPoint(dp));
      await timeSeriesApi.addDataPoints(timeseriesId, convertedDataPoints);
      
      success = `${dataPoints.length} data points added successfully!`;
      dispatch('dataImported');
      bulkDataPoints = '';
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to add data points';
    } finally {
      loading = false;
    }
  }
  
  function handleFileSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      if (file) {
        csvFile = file;
        previewCSV();
      }
    }
  }
  
  function previewCSV() {
    if (!csvFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (typeof e.target?.result !== 'string') return;

        const content = e.target.result;
        previewData = parseCSV(content, csvOptions.timestamp_col, csvOptions.value_col).slice(0, 5);
        error = '';
      } catch (err) {
        error = err instanceof Error ? err.message : 'Failed to parse CSV file';
        previewData = [];
      }
    };
    reader.readAsText(csvFile);
  }

  async function recognizeColumns() {
    if (!csvFile) return;

    try {
      recognizingColumns = true;
      error = '';

      // Call the AI API to recognize columns
      const result = await aiApi.recognizeColumns(csvFile);

      // Update CSV options with recognized columns
      csvOptions.timestamp_col = result.timestamp_column;
      csvOptions.value_col = result.value_column;

      // Preview CSV with the new column names
      previewCSV();

      success = 'Column names detected by AI: ' +
                'Timestamp = ' + result.timestamp_column +
                ', Value = ' + result.value_column;

    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to recognize columns';
    } finally {
      recognizingColumns = false;
    }
  }
  
  async function handleCSVSubmit() {
    if (!csvFile) {
      error = 'Please select a CSV file';
      return;
    }

    try {
      loading = true;
      error = '';
      success = '';

      const response = await timeSeriesApi.uploadCsv(
        timeseriesId,
        csvFile,
        csvOptions.timestamp_col,
        csvOptions.value_col,
        csvOptions.interpolation_method || 'linear'
      );

      success = `Data uploaded successfully! Added ${response.message.split(' ')[1]} data points.`;
      dispatch('dataImported');
      csvFile = null;
      previewData = [];
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to upload CSV file';
    } finally {
      loading = false;
    }
  }
  
  // Watch for changes in CSV options
  $: if (csvFile && (csvOptions.timestamp_col || csvOptions.value_col)) {
    previewCSV();
  }
</script>

<div class="data-import-container card">
  <div class="card-header">
    <h5 class="mb-0">Импорт данных</h5>
  </div>
  
  <div class="card-body">
    <!-- Import method tabs -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'manual' ? 'active' : ''}" 
          on:click={() => activeTab = 'manual'}
        >
          <i class="bi bi-pencil-square me-1"></i>
          Ручной ввод
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'bulk' ? 'active' : ''}" 
          on:click={() => activeTab = 'bulk'}
        >
          <i class="bi bi-list-ul me-1"></i>
          Массовый ввод
        </button>
      </li>
      <li class="nav-item">
        <button 
          class="nav-link {activeTab === 'csv' ? 'active' : ''}" 
          on:click={() => activeTab = 'csv'}
        >
          <i class="bi bi-file-earmark-text me-1"></i>
          Загрузка CSV
        </button>
      </li>
    </ul>
    
    <!-- Error/success messages -->
    {#if error}
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button type="button" class="btn-close" on:click={() => error = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    {#if success}
      <div class="alert alert-success alert-dismissible fade show" role="alert">
        {success}
        <button type="button" class="btn-close" on:click={() => success = ''} aria-label="Close"></button>
      </div>
    {/if}
    
    <!-- Manual entry form -->
    {#if activeTab === 'manual'}
      <div class="tab-content">
        <form on:submit|preventDefault={handleManualSubmit}>
          <div class="mb-3">
            <label for="timestamp" class="form-label">Временная метка</label>
            <input 
              type="datetime-local" 
              class="form-control" 
              id="timestamp" 
              bind:value={manualDataPoint.timestamp}
              required
            />
            <div class="form-text">Для данных с частотой {frequency}. Формат: ГГГГ-ММ-ДД ЧЧ:ММ</div>
          </div>
          
          <div class="mb-3">
            <label for="value" class="form-label">Значение</label>
            <input 
              type="number" 
              class="form-control" 
              id="value" 
              bind:value={manualDataPoint.value}
              step="any"
              required
            />
          </div>
          
          <button 
            type="submit" 
            class="btn btn-primary" 
            disabled={loading}
          >
            {#if loading}
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Adding...
            {:else}
              <i class="bi bi-plus-circle me-2"></i>
              Add Data Point
            {/if}
          </button>
        </form>
      </div>
    {:else if activeTab === 'bulk'}
      <!-- Bulk entry form -->
      <div class="tab-content">
        <form on:submit|preventDefault={handleBulkSubmit}>
          <div class="mb-3">
            <label for="bulkData" class="form-label">Bulk Data Points</label>
            <textarea 
              class="form-control" 
              id="bulkData" 
              rows="5" 
              bind:value={bulkDataPoints}
              placeholder="2023-01-01T12:00:00,100.5&#10;2023-01-02T12:00:00,101.2&#10;2023-01-03T12:00:00,99.8"
              required
            ></textarea>
            <div class="form-text">Enter each data point on a new line in the format: timestamp,value</div>
          </div>
          
          <button 
            type="submit" 
            class="btn btn-primary" 
            disabled={loading}
          >
            {#if loading}
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Adding...
            {:else}
              <i class="bi bi-plus-circle me-2"></i>
              Add Data Points
            {/if}
          </button>
        </form>
      </div>
    {:else if activeTab === 'csv'}
      <!-- CSV upload form -->
      <div class="tab-content">
        <form on:submit|preventDefault={handleCSVSubmit}>
        <div class="mb-3">
          <label for="csvFile" class="form-label">CSV File</label>
          <input 
            type="file" 
            class="form-control" 
            id="csvFile" 
            accept=".csv" 
            on:change={handleFileSelect}
            required
          />
        </div>
        
        <div class="mb-3">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm float-end"
            on:click={recognizeColumns}
            disabled={!csvFile || recognizingColumns}
          >
            {#if recognizingColumns}
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Detecting columns...
            {:else}
              <i class="bi bi-magic me-2"></i>
              Auto-detect columns with AI
            {/if}
          </button>
          <label class="form-label">Column Names</label>
          <div class="form-text mb-2">Specify column names or click Auto-detect to use AI</div>

          <div class="row">
            <div class="col-md-6">
              <label for="timestampCol" class="form-label">Timestamp Column</label>
              <input
                type="text"
                class="form-control"
                id="timestampCol"
                bind:value={csvOptions.timestamp_col}
                required
              />
            </div>

            <div class="col-md-6">
              <label for="valueCol" class="form-label">Value Column</label>
              <input
                type="text"
                class="form-control"
                id="valueCol"
                bind:value={csvOptions.value_col}
                required
              />
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label for="interpolationMethod" class="form-label">Пропущенные значения обработаны методом:</label>
          <select
            class="form-select"
            id="interpolationMethod"
            bind:value={csvOptions.interpolation_method}
          >
            <option value="linear">linear (Линейная интерполяция)</option>
            <option value="time">time (Интерполяция по временному индексу)</option>
            <option value="index">index (Интерполяция по индексу ряда)</option>
            <option value="pad">pad (Заполнение предыдущим значением)</option>
            <option value="nearest">nearest (Ближайшее значение)</option>
            <option value="zero">zero (Заполнение нулями)</option>
            <option value="cubic">cubic (Кубическая интерполяция)</option>
            <option value="spline">spline (Сплайн-интерполяция)</option>
            <option value="none">none (Не обрабатывать пропущенные значения)</option>
          </select>
          <div class="form-text">Выберите метод интерполяции для обработки пропущенных значений</div>
        </div>

        {#if previewData.length > 0}
          <div class="mb-3">
            <h6>Preview Data</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {#each previewData as point}
                    <tr>
                      <td>{point.timestamp}</td>
                      <td>{point.value}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {/if}
        
        <button 
          type="submit" 
          class="btn btn-primary" 
          disabled={loading || !csvFile}
        >
          {#if loading}
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Uploading...
          {:else}
            <i class="bi bi-upload me-2"></i>
            Upload CSV
          {/if}
        </button>
        </form>
      </div>
    {/if}
  </div>
</div>

<style>
  .data-import-container {
    margin-bottom: 1.5rem;
  }
  
  .nav-tabs .nav-link {
    color: #495057;
    cursor: pointer;
  }
  
  .nav-tabs .nav-link.active {
    color: var(--primary-color);
    font-weight: bold;
  }
  
  .form-text {
    font-size: 0.85rem;
    color: #6c757d;
  }
</style>
